import React, { useState, useRef, useEffect, ReactNode } from "react";
import { Flex, Box } from "rebass";
import styled from "styled-components";

export interface DropdownMenuItem {
  id: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
}

interface DropdownMenuProps {
  trigger: ReactNode;
  items: DropdownMenuItem[];
  className?: string;
}

export const DropdownMenu = ({
  trigger,
  items,
  className,
}: DropdownMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTriggerClick = () => {
    setIsOpen(!isOpen);
  };

  const handleItemClick = (item: DropdownMenuItem) => {
    item.onClick();
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <DropdownContainer ref={containerRef} className={className}>
      <TriggerWrapper onClick={handleTriggerClick}>{trigger}</TriggerWrapper>
      {isOpen && (
        <MenuContainer>
          {items.map((item) => (
            <MenuItem key={item.id} onClick={() => handleItemClick(item)}>
              <IconWrapper>{item.icon}</IconWrapper>
              <MenuText>{item.label}</MenuText>
            </MenuItem>
          ))}
        </MenuContainer>
      )}
    </DropdownContainer>
  );
};

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const TriggerWrapper = styled.div`
  cursor: pointer;
`;

const MenuContainer = styled(Flex)`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 1000;
  background: ${({ theme }) => theme.colors.navy200};
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  box-shadow: 0px 0px 16px 0px #00000066;
  border-radius: 8px;
  flex-direction: column;
  min-width: 176px;
  padding: 8px 4px;
`;

const MenuItem = styled(Flex)`
  width: 176px;
  height: 36px;
  align-items: center;
  gap: 16px;
  border-radius: 8px;
  padding: 0 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #2f4656;
  }
`;

const IconWrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.shade300};

  svg {
    width: 20px;
    height: 20px;
  }
`;

const MenuText = styled.span`
  font-family: Barlow;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  letter-spacing: 0%;
  color: ${({ theme }) => theme.colors.shade300};
  white-space: nowrap;
`;
